import type { CalculationCoefficients, LockCalculatorFormValues } from "../model/form";
import {
  DEFAULT_JOIST_TOP_ALIGNMENT,
  FINISH_EVO,
  type ConnectorUpdate,
  type Fastener,
  type Finish,
  type JoistTopAlignment,
  type Lock,
  type LockCalculatorDefinition,
  type LockCalculatorDefinitionUpdateData,
  type LockStop,
  type OperationMode,
} from "../../../../http/lock_calculator/types";

export const nonNilUuid = (id: string | null | undefined): string | null => (id ? id : null);

/** Maps the persisted backend shape to the frontend domain form. */
export const fromCalculationData = (data?: LockCalculatorDefinition): LockCalculatorFormValues => ({
  environment: {
    standardId: data?.standard?.id ?? null,
    serviceClassId: data?.serviceClass?.id ?? null,
    loadDurationClassId: data?.loadDurationClass?.id ?? null,
    // `null` is a meaningful backend value (EC5 forces it — see standardCodeEC5
    // in calculation_service.go) and must not be coalesced into `false`, or a
    // reset()/reload right after an EC5 save flips it back to `false`, which
    // the EnvironmentForm EC5-clearing effect then sets back to `null`,
    // dirtying the form and re-triggering autosave in a loop.
    continuousControl: data ? data.continuousCtrl ?? null : false,
    // The switch is not persisted: it is on exactly when the calculation carries an override.
    customCoefficients:
      data?.kmodOverride != null || data?.gammaMConnectionOverride != null || data?.gammaMAluminiumOverride != null,
    kmodOverride: data?.kmodOverride ?? null,
    gammaMConnectionOverride: data?.gammaMConnectionOverride ?? null,
    gammaMAluminiumOverride: data?.gammaMAluminiumOverride ?? null,
  },
  lateralResistanceConfig: data?.lateralResistanceConfig ?? null,
  upliftResistanceConfig: data?.upliftResistanceConfig ?? null,
  connectorPairCount:
    data?.configuration?.connectors?.length === 2 ? 2 : data?.configuration?.connectors?.length === 1 ? 1 : null,
  connectorPairs: [
    {
      lockItemId: nonNilUuid(data?.configuration?.connectors?.[0]?.lockItem?.item.id),
      finish: (data?.configuration?.connectors?.[0]?.finish as Finish | null) ?? FINISH_EVO,
    },
    {
      lockItemId: nonNilUuid(data?.configuration?.connectors?.[1]?.lockItem?.item.id),
      finish: (data?.configuration?.connectors?.[1]?.finish as Finish | null) ?? FINISH_EVO,
    },
  ],
  headerMember: {
    materialGradeId: data?.headerMaterialGrade?.id ?? null,
    width: data?.headerB ?? null,
    height: data?.headerH ?? null,
    orientation: data?.headerOrientation ?? null,
  },
  joistMember: {
    materialGradeId: data?.joistMaterialGrade?.id ?? null,
    width: data?.joistB ?? null,
    height: data?.joistH ?? null,
    alphaDeg: data?.alphaDeg ?? null,
    betaDeg: data?.betaDeg ?? null,
    connectorTopDistance: data?.connectorTopDistance ?? null,
    connectorTopDistanceDefault: null,
    topAlignment: data?.joistTopAlignment ?? DEFAULT_JOIST_TOP_ALIGNMENT,
    topOffsetFromHeader: data?.joistTopOffset ?? null,
    topOffsetDefault: null,
  },
  fasteners: {
    headerItemId: nonNilUuid(data?.configuration?.headerFastenerItem?.item.id),
    joistItemId: nonNilUuid(data?.configuration?.joistFastenerItem?.item.id),
    inclinedItemId: nonNilUuid(data?.configuration?.inclinedFastenerItem?.item.id),
  },
  lockstopItemId:
    data?.lateralResistanceConfig === 4 ? nonNilUuid(data?.configuration?.lockstopItem?.item.id) : null,
  bom: { connectionCount: data?.connectionCount ?? null, headerLockItemId: nonNilUuid(data?.headerLockItemId) },
  loads: {
    vertical: data?.verticalLoad ?? null,
    uplift: data?.upliftLoad ?? null,
    axial: data?.axialLoad ?? null,
    lateral: data?.lateralLoad ?? null,
  },
  combinedVerification: {
    includeVertical: data?.includeFv ?? false,
    includeUplift: data?.includeFup ?? false,
    includeAxial: data?.includeFax ?? false,
    includeLateral: data?.includeFlat ?? false,
  },
});

/** The connector top distance as a user value: a value equal to the form's
 * default (c_min / 0) is the default itself, so the backend keeps it unset. */
export const userTopDistance = (form: LockCalculatorFormValues): number | null => {
  const { connectorTopDistance, connectorTopDistanceDefault } = form.joistMember;
  return connectorTopDistance === connectorTopDistanceDefault ? null : connectorTopDistance;
};

/** A coefficient override as sent to the backend: the form value while the "custom" switch is on, else
 * null (the normative value). A non-positive value is not a usable coefficient and is treated as unset. */
export const coefficientOverrideForPayload = (
  form: LockCalculatorFormValues,
  key: "kmodOverride" | "gammaMConnectionOverride" | "gammaMAluminiumOverride",
): number | null => {
  if (!form.environment.customCoefficients) return null;
  const value = form.environment[key];
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
};

/** The joist alignment applies to a column header only; any other orientation persists it as unset. */
export const joistTopAlignmentForPayload = (form: LockCalculatorFormValues): JoistTopAlignment | null =>
  form.headerMember.orientation === "column" ? form.joistMember.topAlignment : null;

/** The joist top offset as a user value: offset mode on a column header only, and a value equal to the
 * form's default (the lock's c_min) is the default itself, so the backend keeps it unset. */
export const joistTopOffsetForPayload = (form: LockCalculatorFormValues): number | null => {
  if (joistTopAlignmentForPayload(form) !== "offset") return null;
  const { topOffsetFromHeader, topOffsetDefault } = form.joistMember;
  return topOffsetFromHeader === topOffsetDefault ? null : topOffsetFromHeader;
};

export const deriveActiveCombination = (form: LockCalculatorFormValues): string | null => {
  const axes: string[] = [];
  if (form.combinedVerification.includeVertical) axes.push("vertical");
  if (form.combinedVerification.includeUplift) axes.push("uplift");
  if (form.combinedVerification.includeAxial) axes.push("axial");
  if (form.combinedVerification.includeLateral) axes.push("lateral");
  return axes.length ? axes.join("+") : null;
};

/** Builds the Connectors array for an update payload — its length itself is
 * the connector-pair count (1, 2, or 0/empty when none chosen yet),
 * replacing the old separate nConnectors field. */
const connectorsUpdate = (form: LockCalculatorFormValues): ConnectorUpdate[] => {
  if (form.connectorPairCount == null) return [];
  const connectors: ConnectorUpdate[] = [
    { lockItemId: nonNilUuid(form.connectorPairs[0].lockItemId), finish: form.connectorPairs[0].finish },
  ];
  if (form.connectorPairCount === 2) {
    connectors.push({ lockItemId: nonNilUuid(form.connectorPairs[1].lockItemId), finish: form.connectorPairs[1].finish });
  }
  return connectors;
};

/** The only FE boundary that knows the backend's historic field vocabulary. */
export const toCalculationUpdateData = (
  form: LockCalculatorFormValues,
  operationMode?: OperationMode,
): LockCalculatorDefinitionUpdateData => ({
  operationMode: operationMode ?? null,
  definition: {
    standardId: form.environment.standardId,
    serviceClassId: form.environment.serviceClassId,
    loadDurationClassId: form.environment.loadDurationClassId,
    continuousCtrl: form.environment.continuousControl,
    kmodOverride: coefficientOverrideForPayload(form, "kmodOverride"),
    gammaMConnectionOverride: coefficientOverrideForPayload(form, "gammaMConnectionOverride"),
    gammaMAluminiumOverride: coefficientOverrideForPayload(form, "gammaMAluminiumOverride"),
    lateralResistanceConfig: form.lateralResistanceConfig,
    upliftResistanceConfig: form.upliftResistanceConfig,
    headerOrientation: form.headerMember.orientation,
    connectionCount: form.bom.connectionCount,
    headerLockItemId: nonNilUuid(form.bom.headerLockItemId),
    headerMaterialGradeId: form.headerMember.materialGradeId,
    headerB: form.headerMember.width,
    headerH: form.headerMember.height,
    joistMaterialGradeId: form.joistMember.materialGradeId,
    joistB: form.joistMember.width,
    joistH: form.joistMember.height,
    alphaDeg: form.joistMember.alphaDeg,
    betaDeg: form.joistMember.betaDeg,
    connectorTopDistance: userTopDistance(form),
    joistTopAlignment: joistTopAlignmentForPayload(form),
    joistTopOffset: joistTopOffsetForPayload(form),
    verticalLoad: form.loads.vertical,
    upliftLoad: form.loads.uplift,
    axialLoad: form.loads.axial,
    lateralLoad: form.loads.lateral,
    includeFv: form.combinedVerification.includeVertical,
    includeFup: form.combinedVerification.includeUplift,
    includeFax: form.combinedVerification.includeAxial,
    includeFlat: form.combinedVerification.includeLateral,
  },
  configuration: {
    connectors: connectorsUpdate(form),
    headerFastenerItemId: nonNilUuid(form.fasteners.headerItemId),
    joistFastenerItemId: nonNilUuid(form.fasteners.joistItemId),
    inclinedFastenerItemId: nonNilUuid(form.fasteners.inclinedItemId),
    lockstopItemId: form.lateralResistanceConfig === 4 ? nonNilUuid(form.lockstopItemId) : null,
  },
});

export type InputSignatures = {
  /** The definition-level inputs: what the generated drafts depend on. */
  definition: string;
  /** Definition plus the chosen connectors/fasteners: what the final result depends on. */
  full: string;
  /** Everything sent, loads and combination flags included: what the report prints. */
  report: string;
};

type NormativeCoefficients = Partial<Pick<CalculationCoefficients, "kmodDefault" | "gammaMDefault" | "gammaMAluminiumDefault">>;

/** An override equal to the normative value is the same calculation as none. */
const normalizedOverride = (override: number | null | undefined, normative: number | null | undefined): number | null =>
  override == null || override === normative ? null : override;

/** `definition` and `full` leave out the loads and the combination flags (a load change moves the utilization,
 * not the resistance, and is recomputed live) and the connection count (it only multiplies the BOM quantities);
 * `report` keeps them. An override equal to the normative value reads as none, so switching "custom" on (it
 * starts from those values) is not a change. */
export const deriveInputSignatures = (form: LockCalculatorFormValues, normative: NormativeCoefficients = {}): InputSignatures => {
  const { definition: sent, configuration } = toCalculationUpdateData(form);
  const definition = {
    ...sent,
    kmodOverride: normalizedOverride(sent.kmodOverride, normative.kmodDefault),
    gammaMConnectionOverride: normalizedOverride(sent.gammaMConnectionOverride, normative.gammaMDefault),
    gammaMAluminiumOverride: normalizedOverride(sent.gammaMAluminiumOverride, normative.gammaMAluminiumDefault),
  };
  const {
    verticalLoad: _vertical,
    upliftLoad: _uplift,
    axialLoad: _axial,
    lateralLoad: _lateral,
    includeFv: _includeFv,
    includeFup: _includeFup,
    includeFax: _includeFax,
    includeFlat: _includeFlat,
    connectionCount: _connectionCount,
    // The header lock only adds a BOM row: it moves neither the drafts nor the resistance.
    headerLockItemId: _headerLock,
    ...inputs
  } = definition;
  const definitionSignature = JSON.stringify(inputs);
  return {
    definition: definitionSignature,
    full: JSON.stringify({ inputs, configuration }),
    report: JSON.stringify({ definition, configuration }),
  };
};

// I riferimenti ottimistici riusano l'oggetto GIA' risolto quando l'id non cambia:
// un segnaposto vuoto (code "", length 0, classNumber assente) farebbe rigenerare
// il 3D con dati finti a ogni autosave, finche' non arriva la risposta del server.
const entityRef = <T extends { id: string }>(id: string | null | undefined, current?: T | null): T | null =>
  id ? (current?.id === id ? current : ({ id } as T)) : null;

const lockRef = (id: string | null, current?: Lock | null): Lock | null =>
  id
    ? current?.item.id === id
      ? current
      : ({
          item: { id, code: "", description: "" },
          family: "MIDI",
          width: 0,
          height: 0,
          thickness: 0,
          nModules: 0,
          evoAvailable: false,
          headFastenersCount: 0,
          joistFastenersCount: 0,
        } as Lock)
    : null;

const fastenerRef = (id: string | null, current?: Fastener | null): Fastener | null =>
  id
    ? current?.item.id === id
      ? current
      : ({
          item: { id, code: "", description: "" },
          diameter: 0,
          length: 0,
          headType: "",
          visibility: "hidden",
          order: 0,
        } as Fastener)
    : null;

const lockstopRef = (id: string | null, current?: LockStop | null): LockStop | null =>
  id
    ? current?.item.id === id
      ? current
      : ({ item: { id, code: "", description: "" }, visibility: "hidden", order: 0 } as LockStop)
    : null;

/** Creates the optimistic backend-shaped value used by the calculation cache. */
export const toOptimisticCalculationData = (
  form: LockCalculatorFormValues,
  current: LockCalculatorDefinition,
  calculationId: string,
): LockCalculatorDefinition => ({
  ...current,
  calculationId,
  standard: entityRef<NonNullable<LockCalculatorDefinition["standard"]>>(
    form.environment.standardId,
    current.standard,
  ),
  serviceClass: entityRef<NonNullable<LockCalculatorDefinition["serviceClass"]>>(
    form.environment.serviceClassId,
    current.serviceClass,
  ),
  loadDurationClass: entityRef<NonNullable<LockCalculatorDefinition["loadDurationClass"]>>(
    form.environment.loadDurationClassId,
    current.loadDurationClass,
  ),
  continuousCtrl: form.environment.continuousControl,
  kmodOverride: coefficientOverrideForPayload(form, "kmodOverride"),
  gammaMConnectionOverride: coefficientOverrideForPayload(form, "gammaMConnectionOverride"),
  gammaMAluminiumOverride: coefficientOverrideForPayload(form, "gammaMAluminiumOverride"),
  // Effective values follow the override straight away, so the read-only figures don't lag the autosave.
  kmod: coefficientOverrideForPayload(form, "kmodOverride") ?? current.kmodDefault ?? current.kmod,
  gammaMConnection:
    coefficientOverrideForPayload(form, "gammaMConnectionOverride") ?? current.gammaMConnectionDefault ?? current.gammaMConnection,
  gammaMAluminium: coefficientOverrideForPayload(form, "gammaMAluminiumOverride") ?? null,
  lateralResistanceConfig: form.lateralResistanceConfig,
  upliftResistanceConfig: form.upliftResistanceConfig,
  headerOrientation: form.headerMember.orientation,
  connectionCount: form.bom.connectionCount,
  headerLockItemId: nonNilUuid(form.bom.headerLockItemId),
  headerMaterialGrade: entityRef<NonNullable<LockCalculatorDefinition["headerMaterialGrade"]>>(
    form.headerMember.materialGradeId,
    current.headerMaterialGrade,
  ),
  headerB: form.headerMember.width,
  headerH: form.headerMember.height,
  joistMaterialGrade: entityRef<NonNullable<LockCalculatorDefinition["joistMaterialGrade"]>>(
    form.joistMember.materialGradeId,
    current.joistMaterialGrade,
  ),
  joistB: form.joistMember.width,
  joistH: form.joistMember.height,
  alphaDeg: form.joistMember.alphaDeg,
  betaDeg: form.joistMember.betaDeg,
  connectorTopDistance: userTopDistance(form),
  joistTopAlignment: joistTopAlignmentForPayload(form),
  joistTopOffset: joistTopOffsetForPayload(form),
  configuration: {
    // Conserva i campi gia' risolti (installationLevels, ...): vengono
    // sovrascritti solo quelli che il form puo' aver cambiato.
    ...current.configuration,
    id: current.configuration?.id ?? "",
    connectors:
      form.connectorPairCount == null
        ? []
        : ([0, 1] as const).slice(0, form.connectorPairCount).map((index) => {
            const currentConnector = current.configuration?.connectors?.[index];
            const pair = form.connectorPairs[index];
            return {
              // Gli altri campi del connettore valgono solo se il lock e' lo stesso.
              ...(currentConnector?.lockItem?.item.id === pair.lockItemId ? currentConnector : null),
              lockItem: lockRef(pair.lockItemId, currentConnector?.lockItem),
              finish: pair.finish,
            };
          }),
    headerFastenerItem: fastenerRef(form.fasteners.headerItemId, current.configuration?.headerFastenerItem),
    joistFastenerItem: fastenerRef(form.fasteners.joistItemId, current.configuration?.joistFastenerItem),
    inclinedFastenerItem: fastenerRef(form.fasteners.inclinedItemId, current.configuration?.inclinedFastenerItem),
    lockstopItem:
      form.lateralResistanceConfig === 4
        ? lockstopRef(form.lockstopItemId, current.configuration?.lockstopItem)
        : null,
    result: {
      ...current.configuration?.result,
      activeCombination: deriveActiveCombination(form),
    },
  },
  verticalLoad: form.loads.vertical,
  upliftLoad: form.loads.uplift,
  axialLoad: form.loads.axial,
  lateralLoad: form.loads.lateral,
  includeFv: form.combinedVerification.includeVertical,
  includeFup: form.combinedVerification.includeUplift,
  includeFax: form.combinedVerification.includeAxial,
  includeFlat: form.combinedVerification.includeLateral,
});
